<template>
  <div style="width: 100%" class="fill-height">
    <v-container>
      <v-row class="justify-center">
        <v-col cols="12" sm="11" lg="11" :class="['px-0']">
          <!-- Sticky Header with Filters -->
          <div v-if="listView" class="table-header-filters">
            <v-row dense class="align-center">
              <v-col cols="12" sm="6" md="3" lg="2">
                <v-select
                  v-model="selectedCategories"
                  :items="categories"
                  label="Kategorija"
                  item-text="name"
                  item-value="id"
                  dense
                  outlined
                  hide-details
                  clearable
                  multiple
                  class="filter-select"
                  @change="onSetIdsForFilter"
                >
                  <template v-slot:selection="{ item, index }">
                    <v-chip v-if="index === 0" small color="rgba(57, 137, 201, 0.1)" text-color="#3989C9">
                      <span>{{ item.name }}</span>
                    </v-chip>
                    <span v-if="index === 1" class="grey--text text-caption ml-1">
                      (+{{ selectedCategories.length - 1 }})
                    </span>
                  </template>
                </v-select>
              </v-col>
              <v-col cols="12" sm="6" md="2" lg="2" class="d-flex align-center">
                <v-checkbox
                  v-model="showInactive"
                  label="Prikaži i neaktivne"
                  color="#3989C9"
                  @change="refresh"
                  dense
                  hide-details
                  class="mt-0 pt-0 filter-checkbox"
                ></v-checkbox>
              </v-col>
              <v-col cols="12" sm="8" md="5" lg="4">
                <v-text-field
                  v-model="searchTerm"
                  label="Pretraži artikle"
                  dense
                  outlined
                  hide-details
                  clearable
                  class="filter-select"
                  @input="onSearchInput"
                  @click:clear="clearSearch"
                >
                  <template #append>
                    <v-icon color="#3989C9" small>mdi-magnify</v-icon>
                  </template>
                </v-text-field>
              </v-col>
              <v-col cols="12" sm="4" md="2" lg="1">
                <v-btn
                  class="search-btn"
                  @click="applySearch"
                  :loading="isLoading"
                  :disabled="isLoading"
                  elevation="0"
                  small
                  block
                >
                  <v-icon small class="mr-1">mdi-magnify</v-icon>
                  Pretraži
                </v-btn>
              </v-col>
            </v-row>
          </div>

          <!-- Initial Loading State -->
          <v-row v-if="isLoading && items.length === 0" class="justify-center mt-8 mb-8">
            <v-progress-circular
              indeterminate
              size="64"
              color="#3989C9"
              width="4"
            ></v-progress-circular>
          </v-row>

          <!-- Items List -->
          <div v-if="listView" class="pricelist-rows-body">
            <v-card
              v-for="(item, i) in items"
              :key="i"
              :class="['pricelist-item-card', item.status === 'OK' ? 'status-ok' : item.status === 'PROCESSING' ? 'status-warn' : 'status-failed']"
            >
            <v-row class="ma-0" style="min-height: 60px !important">
              <div
                v-if="density !== 'greater'"
                style="width: 6% !important; display: flex; align-items: center; justify-content: center;"
                :class="[densityPadding]"
              >
                <v-img
                  v-if="item.picture"
                  :src="item.picture"
                  contain
                  :height="density === 'normal' ? '48' : '68'"
                  style="border-radius: 8px;"
                ></v-img>
                <div
                  v-else
                  style="width: 44px; height: 44px; border-radius: 6px; background: #F1F5F9;"
                ></div>
              </div>

              <div
                style="
                  padding-left: 5px;
                  width: 33% !important;
                  padding-bottom: 10px !important;
                "
                :class="[densityPadding]"
              >
                <div class="caption grey--text">Naziv</div>
                <div
                  class="one-liner-text pricelist-item-name"
                  @mouseover="hoverOver(`hover-${item.id}`, item)"
                  @mouseleave="item[`hover-${item.id}`] = false"
                  :id="`hover-${item.id}`"
                  :ref="`hover-${item.id}`"
                >
                  {{ $options.filters.capitalize(item.name) }}
                </div>
                <v-tooltip v-model="item[`hover-${item.id}`]" bottom>
                  <!--Fake activator to avoid an attach property which is not working properly -->
                  <template v-slot:activator="{ on }">
                    <div v-on="on"></div>
                  </template>
                  <div style="max-width: 300px">{{ item.name }}</div>
                </v-tooltip>
              </div>
              <div style="width: 10% !important" :class="[densityPadding]">
                <div class="caption grey--text text-left">Cijena</div>
                <div
                  class="text-left pricelist-cell-price"
                  v-for="p in item.prices"
                  :key="p.currency"
                >
                  <div v-if="item.manual_price_input">- {{ p.currency }}</div>
                  <div v-if="!item.manual_price_input && p.currency !== 'HRKX'">
                    {{ p.price | money(100, p.currency) }}
                  </div>
                </div>
              </div>
              <div style="width: 5% !important" :class="[densityPadding]">
                <div class="caption grey--text text-sm-center text-md-left">PDV</div>
                <div class="pricelist-cell-value text-sm-center text-md-left">{{ showTax(item) }}</div>
              </div>
              <div style="width: 5% !important" :class="[densityPadding]">
                <div class="caption grey--text text-sm-center text-md-left">{{ havePnp(item) ? "PNP" : "" }}</div>
                <div class="pricelist-cell-value text-sm-center text-md-left">{{ havePnp(item) ? showPnp(item) : "" }}</div>
              </div>
              <div style="width: 7% !important" :class="[densityPadding]">
                <div class="caption grey--text text-md-left">Šifra</div>
                <div class="pricelist-cell-value text-md-left">{{ item.code }}</div>
              </div>
              <div style="width: 8% !important" :class="[densityPadding]">
                <div class="caption grey--text text-md-center">Stanje</div>
                <div class="pricelist-cell-value text-center">{{ item.type === "GOODS" ? item.stock : "-" }}</div>
              </div>
              <v-tooltip bottom>
                <template #activator="{ on: on }">
                  <div v-on="on" :class="[densityPadding]" style="width: 7% !important">
                    <div class="caption grey--text">Status</div>
                    <div class="pricelist-cell-value">{{ translateStatus(item.status) }}</div>
                  </div>
                </template>
                {{ getStatusTooltip(item.status) }}
              </v-tooltip>
              <div style="width: 8% !important" :class="[densityPadding]">
                <div class="caption grey--text">Prodaje se</div>
                <div class="pricelist-cell-value">{{ checkActivePrice(item) }}</div>
              </div>
              <div :class="[densityPadding]" style="width: 10% !important">
                <div class="d-flex align-center justify-end">
                  <v-btn
                    v-if="item.status && item.status.includes('oes not exist')"
                    icon small
                    @click="fixItem(item)"
                  >
                    <v-icon small>mdi-auto-fix</v-icon>
                  </v-btn>
                  <v-menu offset-y left>
                    <template #activator="{ on, attrs }">
                      <v-btn icon small v-on="on" v-bind="attrs">
                        <v-icon size="18" color="#94A3B8">mdi-dots-vertical</v-icon>
                      </v-btn>
                    </template>
                    <v-list dense class="pl-kebab-list">
                      <v-list-item @click="openEditForm(item)" class="pl-menu-item">
                        <v-icon small color="#3989C9" class="mr-2">mdi-pencil</v-icon>
                        <v-list-item-title class="pl-menu-title pl-menu-edit">Uredi</v-list-item-title>
                      </v-list-item>
                      <v-divider class="my-1"></v-divider>
                      <v-list-item @click="openPlDeleteDialog(item)" class="pl-menu-item">
                        <v-icon small color="#ef4444" class="mr-2">mdi-delete-outline</v-icon>
                        <v-list-item-title class="pl-menu-title pl-menu-delete">Obriši s cjenika</v-list-item-title>
                      </v-list-item>
                    </v-list>
                  </v-menu>
                </div>
              </div>
            </v-row>
          </v-card>
          </div>

          <!-- Loading Indicator -->
          <v-row v-if="isLoading && items.length > 0" class="justify-center mt-4 mb-4">
            <v-progress-circular
              indeterminate
              size="48"
              color="#3989C9"
              width="3"
            ></v-progress-circular>
          </v-row>

          <!-- Load More Button -->
          <v-row v-if="hasMoreItems && !isLoading" class="justify-center mt-4 mb-4">
            <v-btn
              class="page-nav-btn-header"
              @click="loadMore"
              :disabled="isLoading"
              elevation="2"
            >
              Učitaj više
              <v-icon small class="ml-2">mdi-chevron-down</v-icon>
            </v-btn>
          </v-row>

          <!-- Empty State -->
          <v-row v-if="!isLoading && items.length === 0" class="justify-center mt-8 mb-8">
            <v-col cols="12" class="text-center">
              <v-icon size="64" color="grey lighten-1">mdi-package-variant</v-icon>
              <p class="text-h6 grey--text mt-4">Nema artikala za prikaz</p>
            </v-col>
          </v-row>

        </v-col>
      </v-row>
    </v-container>

    <!-- Grid View (kept for compatibility) -->
    <v-container v-if="!listView">
      <v-row class="d-flex justify-center align-center fill-height ma-0 pa-0">
        <v-card
          class="align-center ma-5"
          v-for="(item, i) in items"
          :key="i"
          hover
          height="300"
          width="250"
        >
          <v-img
            height="150"
            width="250"
            v-if="item.picture"
            :src="item.picture"
          >
          </v-img>
          <v-img height="165" width="250" v-else src="@/assets/no-item.jpg">
          </v-img>
          <div class="mr-4 ml-4">
            <div>
              <h3 class="selling-point-title-text one-liner-text">
                {{ $options.filters.capitalize(item.name) }}
              </h3>
              <p class="grey--text mb-1 selling-point-text one-liner-text">
                Status: {{ item.status }}
              </p>
              <p class="grey--text mb-1 selling-point-text one-liner-text">
                Cijena: {{ item.price | money(100) }}
              </p>
            </div>
          </div>
          <v-divider></v-divider>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn icon>
              <v-icon>mdi-pencil</v-icon>
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-row>
    </v-container>
    <add-pricelist-item
      ref="addPricelistItem"
      @success="refreshPriceList"
    ></add-pricelist-item>
    <add-wh-pricelist-item
      ref="addWhPricelistItem"
      @success="refreshPriceList"
    ></add-wh-pricelist-item>
    <edit-pricelist-item
      ref="editPricelistItem"
      @success="refreshPriceList"
    ></edit-pricelist-item>
    <edit-wh-pricelist-item
      ref="editWhPricelistItem"
      @success="refreshPriceList"
    ></edit-wh-pricelist-item>
    <add-warehouse-item
      ref="addWarehouseItem"
      @success="startFixProcess"
    ></add-warehouse-item>
    <add-menu-item ref="addMenuItem" @refresh="refresh"></add-menu-item>

    <v-dialog v-model="plDeleteDialog" max-width="460" @click:outside="plDeleteDialog = false">
      <v-card class="del-modal-card" style="height: 300px; display: flex; flex-direction: column;">
        <div class="del-modal-header">
          <div class="del-modal-header-gloss"></div>
          <div class="del-modal-header-content">
            <v-icon color="white" size="22" class="mr-2">mdi-delete-outline</v-icon>
            <div>
              <div class="del-modal-title">Ukloni s cjenika</div>
              <div v-if="plItemToDelete" class="del-modal-subtitle">{{ plItemToDelete.name }}</div>
            </div>
            <v-spacer></v-spacer>
            <v-btn icon small @click="plDeleteDialog = false" class="del-modal-close-btn">
              <v-icon color="white" size="20">mdi-close</v-icon>
            </v-btn>
          </div>
        </div>
        <v-card-text class="pt-5 pb-3 px-6" style="flex: 1; overflow-y: auto; min-height: 0;">
          <div v-if="plDeleteLoading" class="d-flex align-center justify-center py-8">
            <v-progress-circular indeterminate color="#3989C9" size="38" width="3"></v-progress-circular>
          </div>
          <div v-else-if="plDeleteError" class="del-info-box del-info-box--error">
            <v-icon size="20" color="#b91c1c" class="mr-3">mdi-alert-circle-outline</v-icon>
            <span>Došlo je do greške. Pokušajte ponovo.</span>
          </div>
          <div v-else class="del-info-box del-info-box--confirm">
            <v-icon size="20" color="#b91c1c" class="mr-3 flex-shrink-0">mdi-alert-remove-outline</v-icon>
            <span>Artikl će biti uklonjen s cjenika. Artikl ostaje u katalogu.</span>
          </div>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions class="pb-5 px-6" style="flex-shrink: 0;">
          <v-spacer></v-spacer>
          <v-btn text class="del-btn-cancel" @click="plDeleteDialog = false">Zatvori</v-btn>
          <v-btn
            v-if="!plDeleteError"
            elevation="0"
            class="del-btn-delete"
            :loading="plDeleteLoading"
            :disabled="plDeleteLoading"
            @click="confirmPlDelete"
          >
            <v-icon small class="mr-2">mdi-delete-outline</v-icon>
            Ukloni s cjenika
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>
<script>
import { df } from '@/plugins/firebase'
import state from '@/state'
import addPricelistItem from '@/modules/point-of-sale/components/AddPricelistItem'
import addMenuItem from '@/modules/point-of-sale/components/menu-item/AddMenuItem'
import addWhPricelistItem from '@/modules/point-of-sale/components/AddWhPricelistItem'
import editPricelistItem from '@/modules/point-of-sale/components/EditPricelistItem'
import editWhPricelistItem from '@/modules/point-of-sale/components/EditWhPricelistItem'
import addWarehouseItem from '@/modules/point-of-sale/components/AddWarehouseItem'
import flow from '@/mixins/flow'
import applicationSettings from '@/mixins/applicationSettings'
import barcodeChecker from '@/mixins/barcodeChecker'
import { collection, query, onSnapshot, orderBy, where, limit, startAfter, getDocs, doc, getDoc, deleteDoc } from 'firebase/firestore'
import triGram from '@/mixins/trigram'

export default {
  name: 'Pricelist',
  inject: ['showLoader', 'hideLoader', 'showMsgBox'],
  mixins: [flow, applicationSettings, barcodeChecker, triGram],
  components: { addMenuItem, addPricelistItem, editPricelistItem, addWarehouseItem, addWhPricelistItem, editWhPricelistItem },
  data: () => ({
    dialog: false,
    loading: false,
    isLoading: false,
    totalArticles: 0,
    multiplier: 0,
    lastiItem: false,
    items: [],
    initialItems: [],
    warehouseModule: false,
    searchEmitDestination: 'pricelistSearch',
    lastDocSnapshot: null,
    filters: [],
    searchObject: undefined,
    showInactive: false,
    searchTerm: '',
    searchTimeout: null,
    itemListeners: [],
    categories: [],
    selectedCategories: [],
    plDeleteDialog: false,
    plDeleteLoading: false,
    plDeleteError: false,
    plItemToDelete: null
  }),
  computed: {
    listView () {
      return state.isListView()
    },
    hasMoreItems () {
      return this.lastDocSnapshot !== null
    }
  },
  mounted () {
    if (state.getCurrentCompany().warehouse === 'module') {
      this.warehouseModule = true
    }

    this.getMultiplier()
    this.getAllCategories()
    this.getItemsNew()
  },
  watch: {
    listView (nv, ov) {
      if (nv !== ov) {
        this.refresh()
      }
    }
  },
  beforeDestroy () {
    this.detachItemListeners()
  },
  methods: {
    refresh () {
      this.lastDocSnapshot = null
      this.detachListeners()
      this.detachItemListeners()
      this.resetHandler()
    },
    onSetIdsForFilter () {
      this.filters = []
      if (this.selectedCategories && this.selectedCategories.length > 0) {
        this.filters.push(this.selectedCategories)
      }
      this.lastDocSnapshot = null
      this.detachItemListeners()
      this.resetHandler()
    },
    async getAllCategories () {
      try {
        const snapshot = await df.doc(`company_categories/${state.getCurrentCompany().id}`).get()

        // Handle both snapshot.exists (property) and snapshot.exists() (function)
        const exists = typeof snapshot.exists === 'function' ? snapshot.exists() : snapshot.exists

        if (snapshot && exists) {
          const document = snapshot.data()

          if (document && document.categories) {
            const cats = []
            for (const property in document.categories) {
              const category = {
                id: document.categories[property].id,
                name: document.categories[property].name
              }
              cats.push(category)
            }
            this.categories = cats.sort((a, b) => a.name.localeCompare(b.name))
          } else {
            // No categories found, set empty array
            this.categories = []
          }
        } else {
          // Document doesn't exist, set empty array
          this.categories = []
        }
      } catch (error) {
        console.error('Error loading categories:', error)
        // Set empty array on error to prevent further issues
        this.categories = []
      }
    },
    resetHandler () {
      this.items = []
      this.lastDocSnapshot = null
      this.getItemsNew()
    },
    onSearchInput () {
      if (this.searchTimeout) {
        clearTimeout(this.searchTimeout)
      }
      this.searchTimeout = setTimeout(() => {
        this.applySearch()
      }, 500)
    },
    clearSearch () {
      this.searchTerm = ''
      this.searchObject = undefined
      this.lastDocSnapshot = null
      this.detachItemListeners()
      this.items = []
      this.getItemsNew()
    },
    applySearch () {
      if (this.searchTerm && this.searchTerm.trim().length > 0) {
        const triGramObject = this.triGram(this.searchTerm.trim())
        this.searchObject = { triGramObject }
      } else {
        this.searchObject = undefined
      }
      this.lastDocSnapshot = null
      this.detachItemListeners()
      this.items = []
      this.getItemsNew()
    },
    translateStatus (status) {
      if (!status) return ''
      if (status.toUpperCase() === 'OK' || status.toUpperCase() === 'PROCESSING') {
        return status
      } else {
        return this.$t('$vuetify.errors.errorTitle')
      }
    },
    getStatusTooltip (status) {
      if (!status) return ''
      if (status.toUpperCase() === 'OK' || status.toUpperCase() === 'PROCESSING') {
        return status
      } else if (status.toUpperCase() === 'DUPLICATE') {
        return this.$t('$vuetify.errors.duplicateItem')
      } else {
        return status
      }
    },
    showTax (item) {
      var pdv = 0
      if (item && item.prices && item.prices.EUR && item.prices.EUR.taxes) {
        item.prices.EUR.taxes.forEach(tax => {
          if (tax.name !== 'PNP') {
            pdv = tax.rate / 100 + '%'
          }
        })
      }
      return pdv
    },
    showPnp (item) {
      var pnp = 0
      if (item && item.prices && item.prices.EUR && item.prices.EUR.taxes) {
        item.prices.EUR.taxes.forEach(tax => {
          if (tax.name === 'PNP') {
            pnp = tax.rate / 100 + '%'
          }
        })
      }
      return pnp
    },
    havePnp (item) {
      var havePnp = false
      if (item && item.prices && item.prices.EUR && item.prices.EUR.taxes) {
        item.prices.EUR.taxes.forEach(tax => {
          if (tax.name === 'PNP') {
            havePnp = true
          }
        })
      }
      return havePnp
    },
    checkActivePrice (item) {
      var active = 'NE'
      if (item.prices && item.active) {
        Object.keys(item.prices).forEach(currency => {
          if (item.prices[currency].active === true) {
            active = 'DA'
          }
        })
      }
      return active
    },
    hoverOver (ref, item) {
      const container = this.$refs[ref]
      if (!container || !container[0]) return
      if (container[0].clientHeight < container[0].scrollHeight) {
        item[ref] = true
      }
    },
    openDialog () {
      this.dialog = true
    },
    openAddMenuItem () {
      this.$refs.addMenuItem.open()
    },
    openAddPricelistItemForm () {
      if (this.warehouseModule) {
        this.$refs.addWhPricelistItem.open()
      } else {
        this.$refs.addPricelistItem.open()
      }
    },
    openEditForm (item) {
      if (this.warehouseModule) {
        this.$refs.editWhPricelistItem.open(item)
      } else {
        this.$refs.editPricelistItem.open(item)
      }
    },
    fixItem (item) {
      this.confirm({
        title: 'Aktiviraj artikl',
        message: 'Da li ste sigurni da želite aktivirati artikl na trenutnom prodajnom mjestu?',
        options: {
          toolbar: true,
          width: 410,
          confirmText: 'Aktiviraj artikl',
          cancelText: this.$t('$vuetify.close')
        }
      }).then(resp => {
        if (resp) {
          this.confirmClose()
          this.showLoader()
          this.$refs.addWarehouseItem.submit(item, true) // first add item to warehouse
        }
      })
    },
    startFixProcess (resp) {
      setTimeout(() => {
        this.$refs.editPricelistItem.open(resp.item, true) // than again add price
        this.$nextTick(() => {
          this.$refs.editPricelistItem.submit(true)
          this.hideLoader()
        })
      }, 2500)
    },

    async refreshPriceList (itemId) {
      // If an item ID is provided, fetch it and either add or update
      if (itemId) {
        try {
          const itemDocRef = doc(df, `location_pricelist/${state.getPointOfSale().id}/items/${itemId}`)
          const itemSnapshot = await getDoc(itemDocRef)

          if (itemSnapshot.exists()) {
            var itemData = { id: itemId, ...itemSnapshot.data() }

            // Format the item data
            if (itemData.stock) {
              itemData.stock = itemData.stock / this.multiplier
              itemData.stock = itemData.stock.toString().replace('.', ',')
            }
            if (itemData.prices) {
              Object.keys(itemData.prices).forEach(key => {
                itemData.prices[key].currency = key
              })
            }

            // Check if item already exists in the list (edit scenario)
            const existingIndex = this.items.findIndex(it => it?.id === itemId)

            if (existingIndex !== -1) {
              // Item exists - this is an edit, update it in place
              this.$set(this.items, existingIndex, itemData)
            } else {
              // Item doesn't exist - this is a new item, add to top
              this.items.unshift(itemData)
              this.$vuetify.goTo(0)
            }
            return
          }
        } catch (error) {
          console.error('Error fetching price list item:', error)
        }
      }
    },
    async getItemsNew () {
      if (this.isLoading) return 0
      this.isLoading = true

      try {
        var triGramObject = this.searchObject?.triGramObject

        const searchConstraints = []
        if (triGramObject) {
          Object.keys(triGramObject).forEach(name =>
            searchConstraints.push(where(`meta.${name}`, '==', true))
          )
        } else {
          searchConstraints.push(orderBy('name', 'asc'))
        }

        if (this.filters.flat().length > 0) {
          const filters = this.filters.flat()
          searchConstraints.push(where('category_ids', 'array-contains-any', filters))
        }

        if (!this.showInactive) {
          searchConstraints.push(where('active', '==', true))
        }

        if (this.lastDocSnapshot) {
          searchConstraints.push(startAfter(this.lastDocSnapshot))
        }

        searchConstraints.push(limit(10))

        const constraints = [
          collection(df, `location_pricelist/${state.getPointOfSale().id}/items`),
          ...searchConstraints
        ]

        const q = query.apply(this, constraints)
        const querySnapshot = await getDocs(q)

        const results = []
        querySnapshot.forEach(docSnapshot => {
          const d = docSnapshot.data()
          d.stock = d.stock / this.multiplier
          d.stock = d.stock.toString().replace('.', ',')
          if (d.prices) {
            Object.keys(d.prices).forEach(key => {
              d.prices[key].currency = key
            })
          }
          results.push({ _id: d.id, ...d })

          // Attach listener to each new item
          if (d.id) {
            this.attachItemListener(d.id)
          }
        })

        // Add new items to existing list
        this.items.push(...results)

        if (querySnapshot.docs.length > 0) {
          this.lastDocSnapshot = querySnapshot.docs[querySnapshot.docs.length - 1]
        } else {
          this.lastDocSnapshot = null
        }

        if (this.$refs.categoriesFilter) {
          this.$refs.categoriesFilter.init(this.items)
        }

        this.isLoading = false
        return querySnapshot.docs.length
      } catch (error) {
        console.error('Error loading items:', error)
        this.isLoading = false
        return 0
      }
    },

    async loadMore () {
      if (!this.hasMoreItems || this.isLoading) return

      const currentItemCount = this.items.length
      await this.getItemsNew()

      // Scroll to the last item before new items (so it appears just below header)
      this.$nextTick(() => {
        const itemCards = document.querySelectorAll('.pricelist-item-card')
        if (itemCards[currentItemCount - 1]) {
          // Scroll with offset to account for sticky header (64px header + some padding)
          const element = itemCards[currentItemCount - 1]
          const yOffset = -70 // Negative offset to position below header
          const y = element.getBoundingClientRect().top + window.pageYOffset + yOffset
          window.scrollTo({ top: y, behavior: 'smooth' })
        }
      })
    },

    attachItemListener (itemId) {
      // Check if listener already exists
      if (this.itemListeners.some(l => l.itemId === itemId)) {
        return
      }

      const itemDocRef = doc(df, `location_pricelist/${state.getPointOfSale().id}/items/${itemId}`)

      const unsubscribe = onSnapshot(itemDocRef, (docSnapshot) => {
        if (docSnapshot.exists()) {
          const updatedItemData = { _id: docSnapshot.id, id: docSnapshot.id, ...docSnapshot.data() }

          // Process stock and prices
          updatedItemData.stock = updatedItemData.stock / this.multiplier
          updatedItemData.stock = updatedItemData.stock.toString().replace('.', ',')
          if (updatedItemData.prices) {
            Object.keys(updatedItemData.prices).forEach(key => {
              updatedItemData.prices[key].currency = key
            })
          }

          // Find and update the item in items array
          const index = this.items.findIndex(item => item.id === itemId || item._id === itemId)
          if (index !== -1) {
            this.$set(this.items, index, updatedItemData)
          }
        }
      }, (error) => {
        console.error(`Error listening to item ${itemId}:`, error)
      })

      this.itemListeners.push({ itemId, unsubscribe })
    },

    detachItemListeners () {
      this.itemListeners.forEach(listener => {
        if (listener && listener.unsubscribe) {
          listener.unsubscribe()
        }
      })
      this.itemListeners = []
    },

    // modifyItems (change) {
    //   if (change.type === 'added') {
    //     if (this.items.some(item =>
    //       (item.id === change.doc.data().id) ||
    //       (item.name.toLowerCase() === change.doc.data().name && item.status === 'PROCESSING')
    //     )) {
    //       this.items = this.items.map(item => {
    //         let ret = item
    //         if (
    //           (item.id === change.doc.data().id) ||
    //           (item.name === change.doc.data().name.toLowerCase() && item.status === 'PROCESSING')
    //         ) {
    //           ret = change.doc.data()
    //           ret.stock = ret.stock / this.multiplier
    //           ret.stock = ret.stock.toString().replace('.', ',')
    //         }
    //         if (ret.prices) {
    //           Object.keys(ret.prices).forEach(key => {
    //             ret.prices[key].currency = key
    //           })
    //         }
    //         return { ...ret, ...{ [`hover-${change.doc.data().id}`]: false } }
    //       })
    //     } else {
    //       const d = change.doc.data()
    //       d.stock = d.stock / this.multiplier
    //       d.stock = d.stock.toString().replace('.', ',')
    //       if (d.prices) {
    //         Object.keys(d.prices).forEach(key => {
    //           d.prices[key].currency = key
    //         })
    //       }
    //       this.items.push({ ...d, ...{ [`hover-${change.doc.data().id}`]: false } })
    //     }
    //   }
    //   if (change.type === 'modified') {
    //     this.items = this.items.map(item => {
    //       let ret = item
    //       if (item.id === change.doc.data().id) {
    //         ret = change.doc.data()
    //         ret.stock = ret.stock / this.multiplier
    //         ret.stock = ret.stock.toString().replace('.', ',')
    //       }
    //       if (ret.prices) {
    //         Object.keys(ret.prices).forEach(key => {
    //           ret.prices[key].currency = key
    //         })
    //       }
    //       return { ...ret, ...{ [`hover-${change.doc.data().id}`]: false } }
    //     })
    //   }
    // },
    // infiniteHandler ($state) {
    //   let search = 'name'
    //   if (this.isBarcodeSearch(this.search)) {
    //     search = 'barcode'
    //   } else if (this.isNumber(this.search)) {
    //     search = 'code'
    //   }

    //   const where = this.search ? this.search.toLowerCase() : ''

    //   let query = df.collection(`location_pricelist/${state.getPointOfSale().id}/items`)
    //     .orderBy(search, 'asc')
    //     .limit(10)
    //     .startAfter(this.lastVisible)

    //   if (where) {
    //     query = query.where(search, '>=', where).where(search, '<=', where + '\uf8ff')
    //   }

    //   const listener = query
    //     .onSnapshot((doc) => {
    //       doc.docChanges().forEach((change) => {
    //         this.modifyItems(change)
    //       })
    //       if (doc && !doc.empty) {
    //         if (doc.docs[doc.docs.length - 1]) {
    //           this.lastVisible = doc.docs[doc.docs.length - 1]
    //         }
    //         $state.loaded()
    //       } else {
    //         $state.complete()
    //       }
    //     }
    //     // (error) => {
    //     // }
    //     )

    //   this.listeners.push(listener)
    // },
    getMultiplier () {
      const listener = df.doc('project_constants/config').onSnapshot(doc => {
        const data = doc.data()
        if (data && data.item_amount && data.item_amount.decimal_multiplier !== undefined) {
          this.config = data
          this.multiplier = data.item_amount.decimal_multiplier
        }
      })
      this.listeners.push(listener)
    },

    openPlDeleteDialog (item) {
      this.plItemToDelete = item
      this.plDeleteError = false
      this.plDeleteDialog = true
    },

    async confirmPlDelete () {
      if (!this.plItemToDelete) return
      this.plDeleteLoading = true
      try {
        const locId = state.getPointOfSale().id
        await df.doc(`location_pricelist/${locId}/items/${this.plItemToDelete.id}`).delete()
        this.items = this.items.filter(i => i.id !== this.plItemToDelete.id)
        this.plDeleteDialog = false
        this.plItemToDelete = null
      } catch (e) {
        console.error('Greška pri brisanju s cjenika:', e)
        this.plDeleteError = true
      } finally {
        this.plDeleteLoading = false
      }
    }
  }
}
</script>

<style scoped>
/* Table Header with Filters - Sticky */
.table-header-filters {
  position: -webkit-sticky;
  position: sticky;
  top: 64px;
  z-index: 10;
  background: #ffffff;
  padding: 14px 16px 14px;
  border-left: 3px solid #00BCD4;
  border-bottom: 1px solid #EEF2F5;
  border-top-left-radius: 8px;
  border-top-right-radius: 8px;
  box-shadow: 0 4px 12px rgba(0,0,0,0.03);
}

/* Row body wrapper */
.pricelist-rows-body {
  background: #F5F7FA;
  padding: 6px 0 2px;
}

/* Item Cards */
.pricelist-item-card {
  border: 1px solid #EEF2F5 !important;
  border-left: 3px solid #E0E0E0 !important;
  border-radius: 6px !important;
  background: #ffffff !important;
  box-shadow: none !important;
  overflow: hidden !important;
  margin-bottom: 4px !important;
  transition: border-left-color 0.15s ease, border-color 0.15s ease, background 0.15s ease !important;
}

.pricelist-item-card:hover {
  border-color: #00BCD4 !important;
  background: #F8FAFB !important;
}

.pricelist-item-card.status-ok     { border-left-color: #4ADE80 !important; }
.pricelist-item-card.status-warn   { border-left-color: #FBBF24 !important; }
.pricelist-item-card.status-failed { border-left-color: #F87171 !important; }

.pricelist-item-name {
  font-size: 15px;
  font-weight: 700;
  color: rgba(0, 0, 0, 0.87);
}

.pricelist-cell-price {
  font-size: 15px;
  font-weight: 700;
  color: rgba(0, 0, 0, 0.87);
}

.pricelist-cell-value {
  font-size: 15px;
  color: rgba(0, 0, 0, 0.87);
}

/* Filter Select Inputs */
.filter-select.v-text-field--outlined >>> fieldset {
  border-color: rgba(57, 137, 201, 0.2) !important;
  border-radius: 8px !important;
}

.filter-select.v-text-field--outlined:hover >>> fieldset {
  border-color: rgba(57, 137, 201, 0.4) !important;
}

.filter-select.v-text-field--outlined.v-input--is-focused >>> fieldset {
  border-color: #3989C9 !important;
  border-width: 2px !important;
}

.filter-select >>> .v-input__slot {
  background: rgba(255, 255, 255, 0.8) !important;
  backdrop-filter: blur(10px) !important;
  min-height: 36px !important;
  border-radius: 8px !important;
}

.filter-select >>> .v-select__selection,
.filter-select >>> .v-text-field__slot input {
  font-size: 13px !important;
  font-weight: 500 !important;
  color: rgba(70, 70, 80, 0.9) !important;
  letter-spacing: 0.3px !important;
}

.filter-select >>> label {
  font-size: 12px !important;
  font-weight: 500 !important;
  color: rgba(57, 137, 201, 0.8) !important;
}

/* Filter Select Dropdown Menu */
.filter-select >>> .v-menu__content {
  background: rgba(255, 255, 255, 0.98) !important;
  backdrop-filter: blur(20px) !important;
  border-radius: 8px !important;
  border: 1px solid rgba(0, 179, 190, 0.15) !important;
  box-shadow: 0 8px 24px rgba(0, 179, 190, 0.12) !important;
  max-height: 300px !important;
}

.filter-select >>> .v-list-item {
  font-size: 13px !important;
  font-weight: 500 !important;
  transition: all 0.2s ease !important;
  min-height: 40px !important;
}

.filter-select >>> .v-list-item:hover {
  background: rgba(57, 137, 201, 0.08) !important;
}

.filter-select >>> .v-list-item--active {
  background: rgba(0, 179, 190, 0.12) !important;
  color: #3989C9 !important;
}

.filter-select >>> .v-simple-checkbox {
  color: #3989C9 !important;
}

/* Filter Checkbox */
.filter-checkbox >>> .v-label {
  font-size: 13px !important;
  font-weight: 500 !important;
  color: rgba(70, 70, 80, 0.9) !important;
  letter-spacing: 0.3px !important;
}

.filter-checkbox >>> .v-input--selection-controls__ripple {
  color: #3989C9 !important;
}

/* Search Button */
.search-btn {
  background: rgba(255, 255, 255, 0.95) !important;
  color: #3989C9 !important;
  border: 1px solid rgba(57, 137, 201, 0.25) !important;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08) !important;
  backdrop-filter: blur(10px) !important;
  border-radius: 8px !important;
  transition: all 0.2s ease !important;
  padding: 0 20px !important;
  height: 36px !important;
  font-weight: 500 !important;
  text-transform: none !important;
  font-size: 13px !important;
  letter-spacing: 0.3px !important;
}

.search-btn:hover {
  background: rgba(255, 255, 255, 1) !important;
  border-color: #3989C9 !important;
  box-shadow: 0 2px 6px rgba(57, 137, 201, 0.2) !important;
  transform: translateY(-1px);
}

.search-btn .v-icon {
  color: #3989C9 !important;
  font-size: 18px !important;
}

/* Load More Button */
.page-nav-btn-header {
  background: rgba(255, 255, 255, 0.95) !important;
  color: #3989C9 !important;
  border: 1px solid rgba(57, 137, 201, 0.25) !important;
  border-radius: 8px !important;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08) !important;
  backdrop-filter: blur(10px) !important;
  transition: all 0.2s ease !important;
  text-transform: none !important;
  font-weight: 500 !important;
  font-size: 13px !important;
  letter-spacing: 0.3px !important;
  padding: 0 16px !important;
  height: 36px !important;
  min-width: 110px !important;
}

.page-nav-btn-header:hover:not(:disabled) {
  background: rgba(255, 255, 255, 1) !important;
  border-color: #3989C9 !important;
  box-shadow: 0 2px 6px rgba(57, 137, 201, 0.2) !important;
  transform: translateY(-1px);
}

.page-nav-btn-header:disabled {
  opacity: 0.5 !important;
  cursor: not-allowed !important;
  background: rgba(245, 245, 245, 0.8) !important;
  border-color: rgba(200, 200, 200, 0.3) !important;
  color: rgba(158, 158, 158, 0.6) !important;
}

/* ── Kebab menu ────────────────────────────────────────────── */
.pl-kebab-list  { padding: 4px 0 !important; min-width: 160px !important; }
.pl-menu-item   { min-height: 36px !important; padding: 0 12px !important; }
.pl-menu-item >>> .v-list-item__content { padding: 0 !important; display: flex !important; align-items: center !important; }
.pl-menu-title  { font-size: 13px !important; font-weight: 500 !important; line-height: 1 !important; }
.pl-menu-edit   { color: #3989C9 !important; }
.pl-menu-delete { color: #ef4444 !important; }

/* ── Delete modal ──────────────────────────────────────────── */
.del-modal-card { border-radius: 16px !important; overflow: hidden !important; border: 1px solid rgba(0,179,190,0.15) !important; box-shadow: 0 8px 32px rgba(0,0,0,0.14) !important; }
.del-modal-header { position: relative; padding: 20px 24px; background: linear-gradient(135deg, rgba(0,179,190,0.55) 0%, rgba(57,137,201,0.5) 50%, rgba(97,84,164,0.55) 100%); overflow: hidden; }
.del-modal-header-gloss { position: absolute; top: 0; left: -100%; width: 60%; height: 100%; background: linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.18) 50%, transparent 100%); pointer-events: none; }
.del-modal-header-content { position: relative; z-index: 1; display: flex; align-items: center; }
.del-modal-title   { color: white !important; font-weight: 700 !important; font-size: 16px !important; letter-spacing: 0.3px; text-shadow: 0 1px 4px rgba(0,0,0,0.15); line-height: 1.3; }
.del-modal-subtitle { color: rgba(255,255,255,0.8) !important; font-size: 12px !important; font-weight: 500 !important; margin-top: 2px; }
.del-modal-close-btn { background: rgba(255,255,255,0.15) !important; }
.del-modal-close-btn:hover { background: rgba(255,255,255,0.25) !important; }
.del-info-box { display: flex; align-items: flex-start; padding: 14px 16px; border-radius: 10px; font-size: 14px; line-height: 1.5; }
.del-info-box--error   { background: #fff1f2; border: 1px solid #fecdd3; color: #7f1d1d; }
.del-info-box--confirm { background: #fff1f2; border: 1px solid #fecdd3; color: #7f1d1d; }
.del-btn-cancel { color: #3989C9 !important; font-weight: 600 !important; font-size: 14px !important; text-transform: none !important; border-radius: 10px !important; height: 42px !important; padding: 0 18px !important; }
.del-btn-delete { background: linear-gradient(135deg, #f43f5e 0%, #dc2626 100%) !important; color: white !important; border-radius: 10px !important; height: 42px !important; padding: 0 22px !important; font-weight: 600 !important; font-size: 14px !important; text-transform: none !important; box-shadow: 0 4px 12px rgba(220,38,38,0.3) !important; }
</style>
